import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Grid,
  Image,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClearOutlined,
  DollarOutlined,
  SearchOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import io from "socket.io-client";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";

dayjs.locale("es");

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const money = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const renderProductImage = (image, size = 38) =>
  image ? (
    <Image
      width={size}
      height={size}
      preview={{ mask: "Ver" }}
      src={image}
      style={{ objectFit: "cover", borderRadius: 10 }}
    />
  ) : (
    <Avatar
      shape="square"
      size={size}
      icon={<ShoppingOutlined />}
      style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 10 }}
    />
  );

const ProductSales = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodMode, setPeriodMode] = useState("month");
  const [selectedDay, setSelectedDay] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedRange, setSelectedRange] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [productKey, setProductKey] = useState(null);
  const [query, setQuery] = useState("");
  const [commissionDrafts, setCommissionDrafts] = useState({});
  const [savingCommissionId, setSavingCommissionId] = useState(null);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/product-sales`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "No se pudieron cargar las ventas");
      setSales(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error(error.message || "No se pudieron cargar las ventas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSales();
    const socket = io(SOCKET_URL);
    socket.on("product-sales:updated", fetchSales);
    return () => {
      socket.off("product-sales:updated", fetchSales);
      socket.disconnect();
    };
  }, [fetchSales]);

  const employeeOptions = useMemo(() => {
    const values = new Map();
    sales.forEach((sale) => {
      const key = String(sale.employeeId || sale.sellerName || "");
      if (key) values.set(key, sale.sellerName || sale.employee?.name || "Empleado");
    });
    return [...values.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sales]);

  const productOptions = useMemo(() => {
    const values = new Map();
    sales.forEach((sale) => {
      const key = String(sale.productId || sale.productCode || sale.productName || "");
      if (key) values.set(key, sale.productName || sale.product?.nombre || "Producto");
    });
    return [...values.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sales]);

  const filteredSales = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return sales.filter((sale) => {
      const soldAt = dayjs(sale.createdAt);
      let matchesPeriod = true;

      if (periodMode === "today") matchesPeriod = soldAt.isSame(dayjs(), "day");
      if (periodMode === "day") matchesPeriod = soldAt.isSame(selectedDay, "day");
      if (periodMode === "month") matchesPeriod = soldAt.isSame(selectedMonth, "month");
      if (periodMode === "range" && selectedRange?.[0] && selectedRange?.[1]) {
        matchesPeriod =
          !soldAt.isBefore(selectedRange[0].startOf("day")) &&
          !soldAt.isAfter(selectedRange[1].endOf("day"));
      }

      const saleEmployeeKey = String(sale.employeeId || sale.sellerName || "");
      const saleProductKey = String(
        sale.productId || sale.productCode || sale.productName || ""
      );
      const matchesEmployee = !employeeId || saleEmployeeKey === employeeId;
      const matchesProduct = !productKey || saleProductKey === productKey;
      const matchesQuery =
        !normalizedQuery ||
        [sale.sellerName, sale.productName, sale.productCode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));

      return matchesPeriod && matchesEmployee && matchesProduct && matchesQuery;
    });
  }, [sales, periodMode, selectedDay, selectedMonth, selectedRange, employeeId, productKey, query]);

  const totals = useMemo(
    () =>
      filteredSales.reduce(
        (result, sale) => ({
          sales: result.sales + 1,
          units: result.units + Number(sale.quantity || 0),
          amount: result.amount + Number(sale.totalAmount || 0),
          commissions: result.commissions + Number(sale.commissionAmount || 0),
        }),
        { sales: 0, units: 0, amount: 0, commissions: 0 }
      ),
    [filteredSales]
  );

  const clearFilters = () => {
    setPeriodMode("month");
    setSelectedDay(dayjs());
    setSelectedMonth(dayjs());
    setSelectedRange(null);
    setEmployeeId(null);
    setProductKey(null);
    setQuery("");
  };

  const saveCommission = async (sale) => {
    const commissionAmount = Number(
      commissionDrafts[sale.id] ?? sale.commissionAmount ?? 0
    );
    if (!Number.isFinite(commissionAmount) || commissionAmount < 0) {
      message.warning("Ingresá un monto de comisión válido");
      return;
    }

    setSavingCommissionId(sale.id);
    try {
      const response = await fetch(`${API_BASE}/product-sales/${sale.id}/commission`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ commissionAmount }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || "No se pudo guardar la comisión");
      setCommissionDrafts((current) => {
        const next = { ...current };
        delete next[sale.id];
        return next;
      });
      message.success("Comisión actualizada");
      fetchSales();
    } catch (error) {
      message.error(error.message || "No se pudo guardar la comisión");
    } finally {
      setSavingCommissionId(null);
    }
  };

  const columns = [
    {
      title: "Fecha y hora",
      dataIndex: "createdAt",
      width: 155,
      render: (value) => (
        <div>
          <Text strong>{dayjs(value).format("DD/MM/YYYY")}</Text>
          <br />
          <Text type="secondary">{dayjs(value).format("HH:mm")}</Text>
        </div>
      ),
    },
    {
      title: "Vendedor",
      dataIndex: "sellerName",
      width: 175,
      render: (value, sale) => (
        <Space>
          <Avatar
            size="small"
            icon={<UserOutlined />}
            style={{ background: sale.employee?.background_color || "#1677ff" }}
          />
          <Text strong>{value || sale.employee?.name || "Empleado"}</Text>
        </Space>
      ),
    },
    {
      title: "Producto",
      dataIndex: "productName",
      width: 220,
      render: (value, sale) => (
        <Space>
          {renderProductImage(sale.product?.imagen)}
          <div>
            <Text strong>{value}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {sale.productCode || "Sin clave"}
            </Text>
          </div>
        </Space>
      ),
    },
    { title: "Cantidad", dataIndex: "quantity", align: "center", width: 90 },
    {
      title: "Precio unitario",
      dataIndex: "unitPrice",
      width: 125,
      render: (value) => money.format(Number(value || 0)),
    },
    {
      title: "Total vendido",
      dataIndex: "totalAmount",
      width: 130,
      render: (value) => <Text strong>{money.format(Number(value || 0))}</Text>,
    },
    {
      title: "Sugerencia",
      key: "suggestion",
      width: 145,
      render: (_, sale) => (
        <Tag color="blue">
          {Number(sale.commissionRate || 0)}% · {money.format(
            Number(sale.totalAmount || 0) * Number(sale.commissionRate || 0) / 100
          )}
        </Tag>
      ),
    },
    {
      title: "Monto para el vendedor",
      key: "commission",
      width: 250,
      render: (_, sale) => (
        <Space.Compact style={{ width: "100%" }}>
          <InputNumber
            min={0}
            precision={2}
            prefix="$"
            value={commissionDrafts[sale.id] ?? Number(sale.commissionAmount || 0)}
            onChange={(value) =>
              setCommissionDrafts((current) => ({ ...current, [sale.id]: value }))
            }
            style={{ width: "100%" }}
          />
          <Button
            type="primary"
            loading={savingCommissionId === sale.id}
            onClick={() => saveCommission(sale)}
          >
            Guardar
          </Button>
        </Space.Compact>
      ),
    },
  ].map((column) => ({
    ...column,
    onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
  }));

  return (
    <div style={{ width: "100%", maxWidth: "none", padding: screens.md ? 12 : 0 }}>
      <Card
        bordered={false}
        style={{
          borderRadius: screens.md ? 22 : 0,
          border: screens.md ? "1px solid #e8edf4" : "none",
          background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 56%, #f4fff9 100%)",
        }}
        styles={{ body: { padding: screens.md ? 28 : 16 } }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/admin/productos")}
          style={{ marginBottom: 18, paddingInline: 0, fontWeight: 700 }}
        >
          Volver a productos
        </Button>
        <Row justify="space-between" align="middle" gutter={[18, 18]}>
          <Col xs={24} lg={13}>
            <Space align="start" size={14}>
              <Avatar
                size={52}
                shape="square"
                icon={<DollarOutlined />}
                style={{ background: "#dcfce7", color: "#15803d", borderRadius: 15 }}
              />
              <div>
                <Text style={{ color: "#15803d", fontWeight: 800, fontSize: 12, letterSpacing: 1.2 }}>
                  CONTROL DE VENTAS
                </Text>
                <Title level={2} style={{ margin: "3px 0" }}>Ventas de productos</Title>
                <Text type="secondary">Filtrá las ventas y administrá las comisiones del personal.</Text>
              </div>
            </Space>
          </Col>
          <Col xs={24} lg={11}>
            <Row gutter={[10, 10]}>
              <Col span={8}><Statistic title="Ventas" value={totals.sales} /></Col>
              <Col span={8}><Statistic title="Vendido" value={totals.amount} precision={2} prefix="$" /></Col>
              <Col span={8}><Statistic title="Comisiones" value={totals.commissions} precision={2} prefix="$" /></Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Card
        title={<Space><CalendarOutlined />Filtros</Space>}
        bordered={false}
        style={{ marginTop: 18, borderRadius: screens.md ? 20 : 0, border: screens.md ? "1px solid #eef2f6" : "none" }}
        extra={<Button icon={<ClearOutlined />} onClick={clearFilters}>Limpiar</Button>}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Segmented
            block
            value={periodMode}
            onChange={setPeriodMode}
            options={[
              { label: "Hoy", value: "today" },
              { label: "Día", value: "day" },
              { label: "Mes", value: "month" },
              { label: "Rango", value: "range" },
              { label: "Todo", value: "all" },
            ]}
          />

          <Row gutter={[12, 12]}>
            {periodMode === "day" ? (
              <Col xs={24} md={8}><DatePicker value={selectedDay} onChange={(value) => value && setSelectedDay(value)} format="DD/MM/YYYY" style={{ width: "100%" }} size="large" /></Col>
            ) : null}
            {periodMode === "month" ? (
              <Col xs={24} md={8}><DatePicker picker="month" value={selectedMonth} onChange={(value) => value && setSelectedMonth(value)} format="MMMM YYYY" style={{ width: "100%" }} size="large" /></Col>
            ) : null}
            {periodMode === "range" ? (
              <Col xs={24} md={12}><RangePicker value={selectedRange} onChange={setSelectedRange} format="DD/MM/YYYY" style={{ width: "100%" }} size="large" /></Col>
            ) : null}
            <Col xs={24} md={8}>
              <Select allowClear showSearch optionFilterProp="label" value={employeeId} onChange={setEmployeeId} placeholder="Todos los vendedores" options={employeeOptions} style={{ width: "100%" }} size="large" suffixIcon={<TeamOutlined />} />
            </Col>
            <Col xs={24} md={8}>
              <Select allowClear showSearch optionFilterProp="label" value={productKey} onChange={setProductKey} placeholder="Todos los productos" options={productOptions} style={{ width: "100%" }} size="large" suffixIcon={<ShoppingOutlined />} />
            </Col>
            <Col xs={24} md={8}>
              <Input allowClear value={query} onChange={(event) => setQuery(event.target.value)} prefix={<SearchOutlined />} placeholder="Buscar vendedor, producto o clave" size="large" />
            </Col>
          </Row>
        </Space>
      </Card>

      <Card
        bordered={false}
        style={{ marginTop: 18, borderRadius: screens.md ? 20 : 0, border: screens.md ? "1px solid #eef2f6" : "none" }}
      >
        <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col><Title level={4} style={{ margin: 0 }}>Detalle de ventas</Title></Col>
          <Col><Tag color="processing">{filteredSales.length} resultados · {totals.units} unidades</Tag></Col>
        </Row>
        {filteredSales.length || loading ? (
          <Table rowKey="id" loading={loading} columns={columns} dataSource={filteredSales} scroll={{ x: 1290 }} pagination={{ pageSize: 15, showSizeChanger: true, pageSizeOptions: [15, 30, 50] }} />
        ) : (
          <Empty description="No hay ventas para los filtros seleccionados" />
        )}
      </Card>
    </div>
  );
};

export default ProductSales;
